import React from "react";
import { FormattedMessage } from "react-intl";
import {
  Box,
  HStack,
  Text,
  useColorMode,
  useMediaQuery,
  VStack,
} from "@chakra-ui/react";
import { useFlowConfig } from "flows/hooks";
import { FormattedMoney } from "components/FormattedMoney";
import { useIsVirtualCurrency } from "components/FormattedMoney/useIsVirtualCurrency";
import { useGetPrimaryWallet } from "../hooks";
import WalletFiatIcon from "../graphics/WalletFiatIcon";
import PendingPayoutsButton from "./PendingPayoutsButton";

/**
 * Component for displaying wallet balance.
 * @returns {JSX.Element} WalletBalance component.
 */
const WalletBalance = ({ onUpdate }) => {
  const flowConfig = useFlowConfig();
  const { colorMode } = useColorMode();
  const [isLargerThan400] = useMediaQuery("(min-width: 400px)");

  const currency = (flowConfig.currency && flowConfig.currency.toUpperCase()) || "USD";

  const { wallet } = useGetPrimaryWallet({ currency });

  // Check if the currency is virtual
  const isVirtualCurrency = useIsVirtualCurrency(wallet && wallet.processing_currency);

  const hideWalletBalance = flowConfig.hideWalletBalance;

  let walletIconOrPendingDetails = isLargerThan400 ? (
    <Box mt="10px"><WalletFiatIcon colorMode={colorMode} height="90px" width="90px" /></Box>
  ) : null; // So long as screen is wide enough, let's start by showing the wallet icon
  walletIconOrPendingDetails = hideWalletBalance ? (
    <PendingPayoutsButton onUpdate={onUpdate} />
  ) : (
    walletIconOrPendingDetails
  ); // if hideWalletBalance is true, we want to show the pending info here instead of with the balance

  return (
    <HStack gap="1" mt="10px">
      {walletIconOrPendingDetails}
      {hideWalletBalance ? null : (
        <VStack gap="1" align="flex-start">
          {wallet ? (
            <Text color={colorMode === "dark" ? "integrator.100" : "integrator.500"} fontWeight="500">
              {isVirtualCurrency ? (
                <FormattedMessage defaultMessage="Earnings" description="Balance of token." />
              ) : (
                <FormattedMessage
                  defaultMessage="Balance"
                  description="Balance in the context of accounting, like the balance of a bank account."
                />
              )}
            </Text>
          ) : (
            <Text as="i" fontSize="sm" color="secondaryText">
              <FormattedMessage defaultMessage="No wallet available" description="user has no available wallet to use" />
            </Text>
          )}
          {wallet ? (
            <FormattedMoney
              fontSize="4xl"
              color={colorMode === "dark" ? "integrator.100" : "integrator.500" }
              fontWeight="400"
              lineHeight="1"
              currency={wallet.processing_currency}
              value={wallet.provider_data.balance}
            />
          ) : null}
          <PendingPayoutsButton onUpdate={onUpdate} />
        </VStack>
      )}
    </HStack>
  );
};

export default WalletBalance;
